import type { PageableContent, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import queryString from 'query-string';
import snakecaseKeys from 'snakecase-keys';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { Configuration } from '../../request/Types';
import GeofenceRequester from '../geofence/GeofenceRequesterV3';
import GrahpQLVehicleRequester from '../vehicle/GrahpQLVehicleRequester';
import { GraphQLVehicleGroupsRequester } from '../vehicleGroup';
import type { TriggerEvent } from './definitions/events';
import type { Rule, RuleMode, TriggerRuleSummary } from './definitions/rules';
import type { ObservationPeriodTriggerRecord, Trigger, TriggerRecord } from './definitions/triggers';
import type { TriggerUserPreferences, TriggerUserPreferencesRecord } from './definitions/users';
import { parseTrigger, parseTriggers } from './temporary';
import type { TriggerEventsFilters, TriggersFilters } from './utils';

const env = Environment.getInstance().getEnvironment();
const vehicleGroupsGraphQLRequester = new GraphQLVehicleGroupsRequester();
const vehicleGraphQLRequester = new GrahpQLVehicleRequester();
const geofencesRequester = new GeofenceRequester({ host: env.geofenceApiHost });

const DEFAULT_GEOFENCES_PAGE_SIZE = 10000;
const DEFAULT_LOAD_VEHICLES_WITH_ODOMETER = true;
const DEFAULT_LOAD_VEHICLES_WITH_DISABLE_STATUS = true;

class AlertsRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public createTriggger = async (trigger: TriggerRecord) => {
    const timeZone = TimeZone.getTimezone();

    const requestData = {
      endpoint: '/',
      method: HttpMethod.POST,
      body: JSON.stringify({
        name: trigger.name,
        condition_id: trigger.conditionId,
        geofence_ids: trigger.geofenceIds,
        vehicle_ids: trigger.vehicleIds,
        vehicle_group_ids: trigger.vehicleGroupIds,
        days_of_week: trigger.daysOfWeek,
        start_time: trigger.startTime,
        end_time: trigger.endTime,
        timezone: timeZone,
        allow_notifications: trigger.allowNotifications,
        min_road_speed_severity: trigger.minRoadSpeedSeverity,
        geofence_speed_limit_in_km_h: trigger.geofenceSpeedLimitInKmH,
        driver_identification_without_driver_recurrence: trigger.driverIdentificationWithoutDriverRecurrence,
        notified_users_channels: snakecaseKeys(trigger.notifiedUsersChannels),
        request_video: trigger.requestVideo,
      }),
    };

    return this.requester.request(requestData);
  };

  public createObservationPeriodTrigger = async (trigger: ObservationPeriodTriggerRecord) => {
    const timeZone = TimeZone.getTimezone();

    const requestData = {
      endpoint: '/device-health-status-observation-period',
      method: HttpMethod.POST,
      body: JSON.stringify({
        timezone: timeZone,
        allow_notifications: trigger.allowNotifications,
        device_ids: trigger.deviceIds,
        timestamp: trigger.timestamp,
        ingestion_time: trigger.ingestionTime,
        device_health_status: trigger.deviceHealthStatus,
        ticket_description: trigger.ticketDescription,
        device_installation_type: trigger.deviceInstallationType,
      }),
    };

    return this.requester.request(requestData);
  };

  public editTrigger = async (triggerId: UUID, trigger: TriggerRecord) => {
    const timeZone = TimeZone.getTimezone();

    const requestData = {
      endpoint: `/${triggerId}`,
      method: HttpMethod.PUT,
      body: JSON.stringify({
        name: trigger.name,
        condition_id: trigger.conditionId,
        geofence_ids: trigger.geofenceIds,
        vehicle_ids: trigger.vehicleIds,
        vehicle_group_ids: trigger.vehicleGroupIds,
        days_of_week: trigger.daysOfWeek,
        start_time: trigger.startTime,
        end_time: trigger.endTime,
        timezone: timeZone,
        allow_notifications: trigger.allowNotifications,
        min_road_speed_severity: trigger.minRoadSpeedSeverity,
        geofence_speed_limit_in_km_h: trigger.geofenceSpeedLimitInKmH,
        driver_identification_without_driver_recurrence: trigger.driverIdentificationWithoutDriverRecurrence,
        notified_users_channels: snakecaseKeys(trigger.notifiedUsersChannels),
        request_video: trigger.requestVideo,
      }),
    };

    return this.requester.request(requestData);
  };

  public getRules = async (mode?: RuleMode): Promise<Rule[]> => {
    const searchParams = queryString.stringify({
      mode,
    });
    const requestData = {
      endpoint: `/rules?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getRule = async (ruleId: UUID): Promise<Rule> => {
    const requestData = {
      endpoint: `/rules/${ruleId}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);
    return camelcaseKeys(data, { deep: true });
  };

  // The fleetId and the logic are temporary, they will be removed when the Janus assets are ready
  public getTriggers = async (filters?: TriggersFilters): Promise<PageableContent<Trigger>> => {
    const searchParams = queryString.stringify({
      ...filters,
      vehicleGroupIds: filters?.vehicleGroupsIds?.join(','),
    });
    const requestData = {
      endpoint: `?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return parseTriggers(camelcaseKeys(data, { deep: true }), {
      vehicleGroups: await vehicleGroupsGraphQLRequester.getVehicleGroups(),
    });
  };

  public getTrigger = async (triggerId: UUID): Promise<Trigger> => {
    const requestData = {
      endpoint: `/${triggerId}`,
      method: HttpMethod.GET,
    };

    const [vehicleGroups, { content: geofences }, vehicles, { data: trigger }] = await Promise.all([
      vehicleGroupsGraphQLRequester.getVehicleGroups(),
      geofencesRequester.getGeofences({
        filters: { size: DEFAULT_GEOFENCES_PAGE_SIZE },
      }),
      vehicleGraphQLRequester.getVehicles(DEFAULT_LOAD_VEHICLES_WITH_ODOMETER, DEFAULT_LOAD_VEHICLES_WITH_DISABLE_STATUS),
      this.requester.request(requestData),
    ]);

    return parseTrigger(camelcaseKeys(trigger, { deep: true }), {
      vehicleGroups,
      geofences,
      vehicles,
    });
  };

  public getTriggersAmount = async (): Promise<number> => {
    const requestData = {
      endpoint: '/',
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);
    return data.content.length;
  };

  public activeTrigger = async (triggerId: UUID, active: boolean): Promise<Trigger> => {
    const requestData = {
      endpoint: `/${triggerId}/active`,
      method: HttpMethod.PUT,
      body: JSON.stringify(active),
    };

    const { data } = await this.requester.request(requestData);
    return camelcaseKeys(data, { deep: true });
  };

  public getTriggerEvents = async (filters: TriggerEventsFilters): Promise<PageableContent<TriggerEvent>> => {
    const params = {
      ...filters,
      ruleId: filters.rule?.id,
      vehicleGroupIds: filters?.vehicleGroupIds?.join(','),
    };

    const searchParams = queryString.stringify({ ...params });
    const requestData = {
      endpoint: `/events?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getTriggerUserPreferences = async (userId: UUID): Promise<TriggerUserPreferences> => {
    const requestData = {
      endpoint: `/users/${userId}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);
    return camelcaseKeys(data, { deep: true });
  };

  public updateTriggerUserPreferences = async (userId: UUID, preferences: TriggerUserPreferencesRecord): Promise<TriggerUserPreferences> => {
    const requestData = {
      endpoint: `/users/${userId}/preferences`,
      method: HttpMethod.PUT,
      body: JSON.stringify(decamelizeKeys(preferences)),
    };

    const { data } = await this.requester.request(requestData);
    return camelcaseKeys(data, { deep: true });
  };

  public getTriggerEventsByRule = async (startTime: string, endTime: string, ruleMode?: RuleMode, vehicleGroupIds?: UUID[]): Promise<TriggerRuleSummary[]> => {
    const searchParams = queryString.stringify({
      startTime,
      endTime,
      ruleMode,
      vehicleGroupIds: vehicleGroupIds?.join(','),
    });
    const requestData = {
      endpoint: `/events/summary-by-rule?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getTriggerNotifications = async (filters: TriggerEventsFilters): Promise<any> => {
    const searchParams = queryString.stringify(filters);
    const requestData = {
      endpoint: `/notifications?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getUnreadNotificationsAmount = async (startTime: string, endTime: string): Promise<number> => {
    const requestData = {
      endpoint: `/notifications/unread-amount?startTime=${startTime}&endTime=${endTime}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);
    return data.amount;
  };

  public markNotificationAsRead = async (id: number, createdAt: string): Promise<void> => {
    const requestData = {
      endpoint: '/notifications/read',
      method: HttpMethod.PUT,
      body: JSON.stringify({ id, created_at: createdAt }),
    };

    await this.requester.request(requestData);
  };

  public markAllNotificationsAsRead = async (startTime: string, endTime: string): Promise<void> => {
    const requestData = {
      endpoint: '/notifications/read-all',
      method: HttpMethod.PUT,
      body: JSON.stringify({ start_time: startTime, end_time: endTime }),
    };

    await this.requester.request(requestData);
  };

  public updateTriggerEventObservation = async (id: string, eventTime: string, observation: string): Promise<void> => {
    const requestData = {
      endpoint: '/events/observation',
      method: HttpMethod.PATCH,
      body: JSON.stringify({ id, event_time: eventTime, observation }),
    };

    await this.requester.request(requestData);
  };

  public deleteTriggerEventObservation = async (id: string, eventTime: string): Promise<void> => {
    const requestData = {
      endpoint: '/events/observation',
      method: HttpMethod.PATCH,
      body: JSON.stringify({ id, event_time: eventTime, observation: '' }),
    };

    await this.requester.request(requestData);
  };
}

export default AlertsRequester;
